/**
 * Where the MBS API lives and which tool is calling it.
 *
 * Inside the Next.js app both come from the build: `NEXT_PUBLIC_API_BASE` is inlined
 * at compile time and `window.MBS_TOOL` is set by the page. A host that installs the
 * packaged build (portal) has neither — the env reference is a bare `process.env`
 * lookup its bundler never defines — so it calls `configureDataFetcher` once at
 * startup instead. Explicit configuration always wins over the build-time values.
 */
export interface IDataFetcherConfig {
    /** Origin plus path prefix of the MBS API, without a trailing slash. */
    apiBase?: string;
    /** Sent as the `mbs_tool` query parameter on every request. */
    tool?: string;
}

let config: IDataFetcherConfig = {};

export const configureDataFetcher = (next: IDataFetcherConfig) => {
    config = { ...config, ...next };
};

/** Test hook: forget any explicit configuration. */
export const resetDataFetcherConfig = () => {
    config = {};
};

export const getApiBase = (): string | undefined => {
    if (config.apiBase) return config.apiBase;
    // Kept as a literal member access so Next.js can inline it at build time.
    if (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_BASE) {
        return process.env.NEXT_PUBLIC_API_BASE;
    }
    return undefined;
};

export const getMbsTool = (): string => {
    if (config.tool) return config.tool;
    if (typeof window !== "undefined") return (window as unknown as { MBS_TOOL?: string }).MBS_TOOL || "";
    return "";
};

/**
 * Shared preamble for the fetchers: the configured base, or a clear error when
 * there is none; and a guard against the double-slash a trailing-slash base plus a
 * leading-slash url would produce.
 */
export const resolveApiBase = (url: string): string => {
    const apiBase = getApiBase();
    if (!apiBase) {
        throw new Error(
            "dataFetcher has no API base: call configureDataFetcher({ apiBase }) or set NEXT_PUBLIC_API_BASE",
        );
    }
    if (apiBase.endsWith("/") && url.startsWith("/")) {
        throw new Error(
            "API base ends with a trailing slash and the url param starts with a trailing slash.",
        );
    }
    return apiBase;
};
